/**
 * Type definitions for ADA 604.5.1 Side Wall Grab Bar validation
 * Rule: The side wall grab bar shall be 42 inches (1065 mm) long minimum,
 * located 12 inches (305 mm) maximum from the rear wall and extending
 * 54 inches (1370 mm) minimum from the rear wall.
 */

import * as THREE from 'three';
import type { OrientationResult } from './orientation';

/**
 * Unit conversion constants
 */
export const UNIT_CONVERSION = {
  FEET_TO_INCHES: 12,
  INCHES_TO_FEET: 1 / 12,
  FEET_TO_MM: 304.8,
  MM_TO_FEET: 1 / 304.8,
  INCHES_TO_MM: 25.4,
  MM_TO_INCHES: 1 / 25.4,
} as const;

/**
 * Parameters for ADA Side Wall Grab Bar validation
 */
export interface ADASideWallGrabBarParams {
  /** Text identifier to find grab bars (default: "Side Grab Bar-3D2") */
  grabBarIdentifier: string;
  
  /** Text identifier to find ADA toilets (default: "ada") */
  adaIdentifier: string;
  
  /** Minimum length requirement in inches (default: 42) */
  minLength: number;
  
  /** Maximum distance from rear wall to nearest end in inches (default: 12) */
  maxDistanceFromRearWall: number;
  
  /** Minimum extension from rear wall to furthest end in inches (default: 54) */
  minExtensionFromRearWall: number;
  
  /** Ray casting distance in feet for wall detection (default: 3) */
  raycastDistance: number;
  
  /** Enable debug visualization */
  enableDebugVisualization: boolean;
  
  /** Measurement tolerance in inches (default: 0.1) */
  measurementTolerance: number;
}

/**
 * Wall information for grab bar validation
 */
export interface WallInfo {
  dbId: number;
  name: string;
  category: string;
  isPartition: boolean;
  boundingBox: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
  distance: number;
  hitPoint?: { x: number; y: number; z: number };
  direction?: 'front' | 'back';
}

/**
 * Grab bar element information
 */
export interface GrabBarElement {
  dbId: number;
  name: string;
  boundingBox: THREE.Box3;
  center: THREE.Vector3;
  length: number;
  orientation?: OrientationResult;
}

/**
 * Measurement details for grab bar validation
 */
export interface GrabBarMeasurements {
  /** Overall length of grab bar in inches */
  overallLength: number;
  
  /** Distance from rear wall to nearest end in inches */
  nearestEndDistance: number;
  
  /** Distance from rear wall to furthest end in inches */
  furthestEndDistance: number;
  
  /** Points used for measurements */
  measurementPoints: {
    nearestEnd: THREE.Vector3;
    furthestEnd: THREE.Vector3;
    rearWallHitPoint: THREE.Vector3;
  };
  
  /** Method used to determine grab bar end points */
  orientationMethod: {
    /** Which method was used: 'pca' or 'bounding-box' */
    method: 'pca' | 'bounding-box';
    /** Human-readable description of the method */
    description: string;
    /** Confidence level if PCA was used (0-1) */
    confidence?: number;
  };
  
  /** Grab bar physical measurements for surface calculations */
  grabBarDimensions?: {
    /** Grab bar radius/thickness in inches */
    radiusInches: number;
    /** Measurement type used */
    measurementType: 'surface-to-surface';
  };
}

/**
 * Validation result for a single grab bar
 */
export interface GrabBarValidationResult {
  /** Database ID of the grab bar */
  grabBarDbId: number;
  
  /** Name of the grab bar element */
  grabBarName: string;
  
  /** Detected rear wall information */
  rearWall: WallInfo | null;
  
  /** Measured distances and points */
  measurements: GrabBarMeasurements | null;
  
  /** Validation status */
  status: 'pass' | 'warning' | 'fail' | 'error';
  
  /** Human-readable message */
  message: string;
  
  /** Detailed validation checks */
  validationChecks: {
    lengthCheck: {
      passed: boolean;
      measured: number;
      required: number;
      message: string;
    };
    proximityCheck: {
      passed: boolean;
      measured: number;
      maximum: number;
      message: string;
    };
    extensionCheck: {
      passed: boolean;
      measured: number;
      minimum: number;
      message: string;
    };
  } | null;
  
  /** Additional details for debugging */
  details?: {
    orientationConfidence?: number;
    raycastHits?: number;
    processingTime?: number;
    error?: string;
    toiletDbId?: number;
    toiletName?: string;
  };
}

/**
 * Complete validation report for ADA Side Wall Grab Bar
 */
export interface ADASideWallGrabBarReport {
  /** Timestamp of validation */
  timestamp: string;
  
  /** Parameters used for validation */
  parameters: ADASideWallGrabBarParams;
  
  /** Individual grab bar results */
  results: GrabBarValidationResult[];
  
  /** Summary statistics */
  summary: {
    totalGrabBars: number;
    passed: number;
    warnings: number;
    failed: number;
    errors: number;
    complianceRate: number;
  };
  
  /** Performance metrics */
  performance: {
    totalTime: number;
    avgTimePerGrabBar: number;
  };
}

/**
 * Ray casting visualization data
 */
export interface RayVisualization {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  distance: number;
  hit: boolean;
  hitPoint?: THREE.Vector3;
  color: string;
  label?: string;
}

/**
 * Debug visualization data
 */
export interface DebugVisualization {
  grabBarDbId: number;
  orientationArrow?: {
    origin: THREE.Vector3;
    direction: THREE.Vector3;
    length: number;
    color: string;
  };
  rays: RayVisualization[];
  measurementLines: Array<{
    start: THREE.Vector3;
    end: THREE.Vector3;
    color: string;
    label: string;
  }>;
}

/**
 * Default parameters for ADA Side Wall Grab Bar validation
 */
export const DEFAULT_ADA_SIDE_WALL_GRAB_BAR_PARAMS: ADASideWallGrabBarParams = {
  grabBarIdentifier: 'Side Grab Bar-3D2',
  adaIdentifier: 'Unhousted1',
  minLength: 42, // inches
  maxDistanceFromRearWall: 12, // inches
  minExtensionFromRearWall: 54, // inches
  raycastDistance: 3, // feet
  enableDebugVisualization: false,
  measurementTolerance: 0.1, // inches
};